import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { BsCircleFill, BsPlusCircle } from 'react-icons/bs';
import { BiError } from 'react-icons/bi';
import { EngineIcon, engineLabels } from 'Utils';
import { getDisplayDate, getDisplayTime } from 'Utils';
import { BRAND, INK, RADIUS, STATUS } from 'Utils/styleTokens';
import AppContext from 'Utils/AppContext';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { DataSourceContext } from '../Utils';
import { findDedicatedSchemaSource, createSchemaChildSource } from '../datasourcesApi';

const TestResults = (props) => {
  const { connections, loadConnectionsHandler, setNotification } = useContext(AppContext);
  const { connection } = useContext(DataSourceContext);
  const testResults = props.testResults || {};
  const displayName = testResults.dataSource || testResults.connection || props.connectionName;
  const schemas = testResults.schemas || testResults.databases;
  // Worker-classified engine-internal schemas (sys, INFORMATION_SCHEMA, SQL Server db_* role
  // schemas, pg_catalog, Oracle-maintained owners, …). They stay in the list so the operator
  // sees everything the account can reach, but get no "Create source" action.
  const systemSchemas = new Set(
    Array.isArray(testResults.systemSchemas)
      ? testResults.systemSchemas.map((s) => String(s).toLowerCase())
      : []
  );
  const isSystemSchema = (name) => systemSchemas.has(String(name).toLowerCase());
  const timestamp = testResults.timestamp;
  const engine = props.type;
  const version = testResults.db_version;
  const status = testResults.status || testResults.jobRunState;
  const message = testResults.message;

  // Per-schema "create a dedicated source" action. Offered only when the test reported a
  // real schema list (not the mysql/mongo databases fallback) with more than one entry,
  // for a saved field-mode row — mysql has no schema field in the editor, and a JDBC-URL
  // row's schema lives inside the URL string. Schemas that already have a dedicated row
  // on the same physical database (this source included) render a link to it instead.
  const schemaListIsSchemas = !!(testResults.schemas && testResults.schemas.length);
  const canOfferSchemaSources = schemaListIsSchemas
    && testResults.schemas.length > 1
    && connection && connection.id != null
    && String(connection.type || '').toLowerCase() !== 'mysql'
    && connection._connectionMode !== 'JDBC_URL'
    && !connection.jdbcUrl;

  // Name-popup state: createTarget is the schema being created (null = popup closed).
  const [createTarget, setCreateTarget] = useState(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const openCreateDialog = (schema) => {
    setCreateTarget(schema);
    setNewName(`${connection.name || ''}_${schema}`.replace(/\s+/g, '_'));
    setCreateError(null);
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateTarget(null);
  };

  const nameHasSpaces = /\s/.test(newName);
  const canCreate = newName.trim() !== '' && !nameHasSpaces;

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      // Credentials are inherited server-side from this source (parentDataSourceId) —
      // the user never re-enters the password.
      await createSchemaChildSource(connection, createTarget, newName.trim());
      await loadConnectionsHandler();
      setNotification({
        header: 'Data source created',
        body: (
          <span>
            "{newName.trim()}" created for the {createTarget} schema. Credentials were
            inherited from {connection.name}.
          </span>
        ),
      });
      setCreateTarget(null);
    } catch (e) {
      setCreateError(e && e.message ? e.message : 'Failed to create the data source.');
    }
    setCreating(false);
  };

  let statusColor = null;
  let statusText = null;

  if (status === 'FAILED' || status === 'ERROR') {
    statusColor = STATUS.danger.dot;
    statusText = 'Data source failed.';
  }

  if (status === 'SUCCESS' || status === 'SUCCEEDED') {
    statusText = 'Data source succeeded.';
    statusColor = STATUS.success.dot;
  }

  return (
    <div className='p-3'>
      <div className='text-left'>
        <h5 className='mb-3'>{displayName}</h5>
        <div style={{ fontSize: '14px' }} className='mb-3'>
          <span className='mr-2'>
            <EngineIcon engine={engine} /> {engineLabels[engine]} {version}
          </span>
          { status && statusText && statusColor && (
            <span>
              <BsCircleFill className='mr-2' color={statusColor} />
              { statusText }
            </span>
          )}
        </div>
        <div style={{ fontSize: '14px' }}>
          { timestamp && (
            <div className='mb-3'>
              Data source last tested on {getDisplayDate(timestamp)} at {getDisplayTime(timestamp)}
            </div>
          )}

          { schemas && schemas.length > 0 && (
            <div>
              <div style={{ fontSize: '16px', fontWeight: 600 }} className='mb-4'>Schemas reported by JDBC metadata</div>
              <div className='d-flex flex-wrap'>
                { schemas.map((name, i) => {
                  const system = isSystemSchema(name);
                  const dedicated = canOfferSchemaSources && !system
                    ? findDedicatedSchemaSource(connection, name, connections)
                    : null;
                  return (
                    <div key={i} className='w-50 mb-3 pr-3'>
                      <div
                        style={{
                          border: `1px solid ${INK[150]}`,
                          borderRadius: `${RADIUS.sm}px`,
                          padding: '8px 12px',
                        }}
                      >
                        <div>
                          <BsCircleFill size='8px' color={system ? INK[300] : BRAND.violet} className='mr-2' /> {name}
                        </div>
                        { system && (
                          <div className='mt-1' style={{ fontSize: '12px', color: INK[500] }}>
                            System schema
                          </div>
                        )}
                        { canOfferSchemaSources && !system && (
                          dedicated ? (
                            <div className='mt-1' style={{ fontSize: '12px', color: INK[500] }}>
                              { dedicated.id === connection.id ? (
                                <>This data source</>
                              ) : (
                                <>
                                  Covered by{' '}
                                  <DiscoveryTooltip
                                    placement='top'
                                    text={`Open the data source dedicated to the ${name} schema.`}
                                  >
                                    <Link to={`/sources/source-editor/${encodeURIComponent(dedicated.id)}`}>
                                      {dedicated.name}
                                    </Link>
                                  </DiscoveryTooltip>
                                </>
                              )}
                            </div>
                          ) : (
                            <DiscoveryTooltip
                              placement='top'
                              text={`Create a data source for this same database, dedicated to the ${name} schema.`}
                            >
                              <Button
                                variant='link'
                                size='sm'
                                className='p-0 mt-1'
                                style={{ fontSize: '12px' }}
                                onClick={() => openCreateDialog(name)}
                              >
                                <BsPlusCircle size='11px' className='mr-1' />
                                Create source
                              </Button>
                            </DiscoveryTooltip>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          { (status === 'FAILED' || status === 'ERROR') && message && (
            <div className='mt-4'>
              <h5 className='mb-2' style={{ fontSize: '18px' }}>Data source failed <BiError className='ml-2' color={INK[500]} /></h5>
              { message }
            </div>
          )}
        </div>
      </div>

      <Modal show={createTarget != null} onHide={closeCreateDialog} backdrop='static' keyboard={!creating}>
        <Modal.Header closeButton={!creating}>
          <Modal.Title style={{ fontFamily: 'Rubik', fontSize: 18 }}>
            Create schema source
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && (
            <Alert variant='danger' style={{ fontFamily: 'Rubik', fontSize: 14 }}>
              {createError}
            </Alert>
          )}
          <Form.Group>
            <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
              Data Source Name
            </Form.Label>
            <Form.Control
              type='text'
              value={newName}
              disabled={creating}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='Data source name'
              isInvalid={nameHasSpaces}
            />
            <Form.Control.Feedback type='invalid'>
              Data source names must not include spaces.
            </Form.Control.Feedback>
            <Form.Text className='text-muted'>
              A new data source for this same database will be created, dedicated to
              the {createTarget} schema. Credentials are inherited from this data source.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' onClick={closeCreateDialog} disabled={creating}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleCreate} disabled={creating || !canCreate}>
            {creating ? (
              <>
                <Spinner animation='border' size='sm' className='mr-2' />
                Creating…
              </>
            ) : (
              'Create'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TestResults;
